import { animations, colors, fontWeights, mixins } from './theme/default';
import { css } from '@emotion/react';
import { rem } from 'polished';

const stylesObj = {
  'roadmap-elektromobilitaet': {
    global: css`
      body {
        background-color: ${colors.blue100};

        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        a {
          color: ${colors.blue900};
        }
      }
    `,
    typography: {
      h1: css`
        font-weight: ${fontWeights.medium};
      `,
    },
    link: {
      tile: css`
        background-color: ${colors.blue700};
        border: none;
      `,
      emphasizedImage: (hasBackgroundImage: boolean) => css`
        background-color: ${colors.blue700};
        ${!hasBackgroundImage &&
        css`
          color: ${colors.primary};
        `}
      `,
    },
    moduleHeader: {
      mainTitle: css`
        color: ${colors.blue900};
      `,
      leadText: css`
        color: ${colors.black};
      `,
    },
    teaserTools: {
      tool: css`
        background: ${colors.blue800};
      `,
      main: css`
        color: ${colors.white};
      `,
    },
    teaserCard: {
      default: {
        content: css`
          border-color: ${colors.nightBlue700};
        `,
      },
    },
    slider: css`
      .swiper {
        .swiper-pagination {
          .swiper-pagination-bullet {
            border: ${rem(1)} solid ${colors.blue800};
            background: ${colors.white};

            &-active {
              background: ${colors.blue800};
            }
          }
        }
      }
    `,
    modal: {
      dialog: css`
        background: ${colors.blue100};
      `,
    },
    counter: {
      item: css`
        border-color: ${colors.nightBlue700};
        color: ${colors.nightBlue700};
      `,
    },
    accordion: {
      accordionItem: css`
        border-bottom-color: ${colors.nightBlue700};
      `,
    },
    imageWithCaption: {
      caption: css`
        color: ${colors.blue900};
      `,
    },
    contactBox: {
      content: css`
        box-shadow: none;
        border: ${rem(1)} solid ${colors.nightBlue700};
      `,
    },
    header: {
      scrolled: css`
        background-color: ${colors.blue100};
      `,
      active: css`
        background-image: linear-gradient(${colors.blue100}, ${colors.blue100});
      `,
    },
    headerLogo: css`
      a {
        width: auto;
        height: 80%;

        svg {
          width: auto;
          height: 100%;
        }
      }
    `,
    navigation: {
      footer: css`
        border-color: ${colors.primary};
      `,
      iconLink: css`
        background-color: ${colors.blue700};
      `,
      emphasizedLink: css`
        background-color: ${colors.blue700};
      `,
      navTitle: css`
        color: ${colors.primary};

        strong {
          display: none;
        }

        border-bottom-color: currentColor;
      `,
      rowBox: css`
        nav {
          ${mixins.MQ('wide')} {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      `,
    },
  },
  'fahr-mit-dem-strom': {
    global: css`
      h1,
      h2,
      h3 {
        color: ${colors.primary};
      }
    `,
    typography: {
      h1: css`
        font-weight: ${fontWeights.black};
      `,
      h2: css`
        font-weight: ${fontWeights.black};
      `,
      h3: css`
        font-weight: ${fontWeights.black};

        article & {
          font-weight: ${fontWeights.medium};
        }
      `,
    },
    link: {
      tile: css`
        background-color: ${colors.greenLight};
        border: none;
      `,
      emphasizedImage: (hasBackgroundImage: boolean) => css`
        background-color: ${colors.greenLight};

        ${!hasBackgroundImage &&
        css`
          color: ${colors.primary};
        `}
      `,
    },
    fact: {
      fact: css`
        color: #252525;

        h3 {
          color: ${colors.black};

          ${mixins.responsiveStyles({
            'font-size': { zero: 18, medium: 24 },
            'line-height': { zero: 22, medium: 34 },
          })}
        }

        p {
          color: #252525;

          ${mixins.responsiveStyles({
            'font-size': { zero: 16, medium: 20 },
            'line-height': { zero: 28, medium: 32 },
          })}
        }

        hr {
          background-color: ${colors.black};
          opacity: 1;
        }
      `,
    },
    headerLogo: css`
      svg {
        fill: ${colors.nightBlue700};
      }
    `,
    navigation: {
      iconLink: css`
        background-color: ${colors.greenLight};
      `,
      emphasizedLink: css`
        background-color: ${colors.greenLight};
      `,
    },
    accordion: {
      counter: (bgColor: string) => css`
        &:after {
          font-weight: 900;
          border: none;
          color: white;
          background: ${bgColor};
        }
      `,
    },
  },
  ladestationinfrastruktur: {
    global: css`
      body {
        background-color: ${colors.primarySunYellow100};
        color: ${colors.black};
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      a {
        color: ${colors.secondarySoftPurple800};
      }
    `,
    link: {
      tile: css`
        background-color: ${colors.purple800};
        border: none;
      `,
      emphasizedImage: () => css`
        background-color: ${colors.purple800};
      `,
    },
    button: {
      default: css`
        background: ${colors.secondarySoftPurple800};
        border: none;
        box-shadow: none;
        overflow: hidden;
        color: ${colors.white};

        &:hover,
        &:focus,
        &:active {
          background: ${colors.primarySunYellow800};
          color: ${colors.secondarySoftPurple800};
        }
      `,
      outlined: css`
        background: none;
        border-color: ${colors.secondarySoftPurple800};
        color: ${colors.secondarySoftPurple800};

        &:hover,
        &:focus,
        &:active {
          border-color: ${colors.secondarySoftPurple800};
          background: ${colors.secondarySoftPurple800};
          color: ${colors.white};
        }
      `,
    },
    breadcrumb: css`
      li {
        a {
          color: ${colors.secondarySoftPurple800};
        }

        &:not(:last-child) {
          &::after {
            color: ${colors.purple400};
          }
        }
      }
    `,
    contactBox: {
      content: css`
        background: ${colors.primarySunYellow700};
      `,
      link: css`
        color: ${colors.secondarySoftPurple800};

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    quote: {
      quote: css`
        color: ${colors.secondarySoftPurple800};
      `,
      caption: css`
        color: ${colors.secondarySoftPurple800};
      `,
    },
    fact: {
      fact: css`
        color: ${colors.black};

        h3 {
          color: ${colors.secondarySoftPurple800};

          ${mixins.responsiveStyles({
            'font-size': { zero: 18, medium: 24 },
            'line-height': { zero: 22, medium: 34 },
          })}
        }

        p {
          color: ${colors.black};

          ${mixins.responsiveStyles({
            'font-size': { zero: 16, medium: 20 },
            'line-height': { zero: 28, medium: 32 },
          })}
        }

        hr {
          background-color: ${colors.black};
          opacity: 1;
        }
      `,
    },
    moduleHeader: {
      mainTitle: css`
        color: ${colors.secondarySoftPurple800};
      `,
      leadText: css`
        color: ${colors.black};
      `,
    },
    accordion: {
      title: css`
        color: ${colors.secondarySoftPurple800};
      `,
      accordionItem: css`
        border-bottom-color: ${colors.secondarySoftPurple400};
      `,
      counter: () => css`
        &:after {
          border-color: ${colors.primarySunYellow800};
          color: ${colors.primarySunYellow800};
          font-weight: ${fontWeights.medium};
        }
      `,
      img: css`
        ${mixins.responsiveStyles({
          width: { zero: rem(48), large: rem(48) },
          height: { zero: rem(48), large: rem(48) },
        })}

        margin-top: ${rem(-8)};
        margin-bottom: ${rem(-8)};
      `,
    },
    toggleIcon: {
      icon: css`
        svg path {
          stroke: ${colors.primarySunYellow800};
        }
      `,
    },
    teaserTools: {
      tool: css`
        background: ${colors.secondarySoftPurple800};
      `,
      main: css`
        color: ${colors.white};
        font-size: ${rem(18)};
        line-height: ${rem(26)};
      `,
      title: css`
        hyphens: auto;
      `,
    },
    teaserStorys: {
      title: css`
        hyphens: auto;
      `,
    },
    scrollToTop: {
      button: css`
        color: ${colors.secondarySoftPurple800};
      `,
    },
    styledHr: {
      hr: css`
        background-color: ${colors.secondarySoftPurple200};
      `,
    },
    slider: css`
      .swiper {
        .swiper-pagination {
          .swiper-pagination-bullet {
            border: ${rem(1)} solid ${colors.secondarySoftPurple800};
            background: ${colors.white};

            &-active {
              background: ${colors.secondarySoftPurple800};
            }
          }
        }
      }
    `,
    genericTeaserModule: {
      moreLink: css`
        color: ${colors.secondarySoftPurple800};

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    teaserCard: {
      default: {
        content: css`
          border-color: ${colors.secondarySoftPurple200};
        `,
        date: css`
          color: ${colors.secondarySoftPurple600};
        `,
        link: css`
          color: ${colors.secondarySoftPurple800};
        `,
      },
    },
    teaserNews: {
      background: css`
        background: ${colors.primarySunYellow300};
        opacity: 1;
      `,
      moreLink: css`
        color: ${colors.secondarySoftPurple800};

        &:hover {
          color: ${colors.secondarySoftPurple800};
        }
      `,
    },
    newsletter: {
      content: css`
        color: ${colors.secondarySoftPurple800};
        background-color: ${colors.primarySunYellow700};

        h2 {
          color: inherit;
        }

        canvas {
          display: none;
        }

        a {
          background: ${colors.secondarySoftPurple800};
          color: ${colors.white};

          &:hover,
          &:focus,
          &:active {
            background: ${colors.primarySunYellow800};
            color: ${colors.secondarySoftPurple800};
          }
        }
      `,
    },
    teaserCampaign: {
      lead: css`
        font-weight: ${fontWeights.medium};
      `,
      link: css`
        color: ${colors.secondarySoftPurple800};
      `,
    },
    searchToolbar: {
      button: css`
        svg path {
          transition: stroke ${animations.default};
        }

        &:hover,
        &:focus {
          svg path {
            stroke: ${colors.secondarySoftPurple800};
          }
        }
      `,
      input: css`
        input {
          border-color: transparent;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      `,
    },
    navSearch: {
      button: (hamburgerActive: boolean) => css`
        color: ${colors.secondarySoftPurple800};

        svg path {
          stroke: ${colors.secondarySoftPurple800};
        }

        &:hover,
        &:focus {
          color: ${colors.secondarySoftPurple800};
        }

        ${hamburgerActive &&
        css`
          color: ${colors.white};

          &:hover,
          &:focus {
            color: ${colors.white};
          }

          svg path {
            stroke: ${colors.white};
          }
        `}
      `,
      searchToolbar: (hamburgerActive: boolean) => css`
        ${mixins.responsiveStyles({
          top: { zero: rem(0), medium: rem(2) },
          right: { zero: rem(-1), medium: rem(1) },
        })}

        input {
          border-color: ${colors.secondarySoftPurple200};
        }

        ${hamburgerActive &&
        css`
          button {
            background: ${colors.secondarySoftPurple600};
          }
        `}
      `,
    },
    event: {
      event: css`
        border-color: ${colors.secondarySoftPurple200};
        color: ${colors.secondarySoftPurple800};
      `,
      date: css`
        color: ${colors.secondarySoftPurple800};
      `,
      provider: css`
        color: ${colors.secondarySoftPurple800};
      `,
      link: css`
        color: ${colors.secondarySoftPurple800};
      `,
    },
    imageWithCaption: {
      caption: css`
        color: ${colors.blue900};
      `,
    },
  },
  'erneuerbar-heizen': {
    global: css`
      body {
        color: ${colors.black};
      }
    `,
    breadcrumb: css`
      li a {
        color: ${colors.black};
      }
    `,
    hamburger: css`
      color: ${colors.black};
    `,
    headerLogo: css`
      ${mixins.responsiveStyles({
        width: { zero: 160, medium: 200 },
      })}
    `,
    link: {
      tile: css`
        border: none;
      `,
      emphasizedImage: () => css`
        background-color: transparent;
      `,
      icon: css`
        path {
          fill: ${colors.orangeDark};
          stroke: ${colors.orangeDark};
        }
      `,
    },
    navigation: {
      emphasizedLink: css`
        background-color: transparent;
        box-shadow: 0px 3px 20px 0px rgba(0, 0, 0, 0.05);
        color: ${colors.black};
      `,
      iconLink: css`
        background-color: transparent;
        box-shadow: 0px 3px 20px 0px rgba(0, 0, 0, 0.05);
        color: ${colors.black};
      `,
    },
    accordion: {
      counter: () => css`
        &:after {
          border-color: ${colors.orangeDark};
          color: ${colors.orangeDark};
        }
      `,
    },
    toggleIcon: {
      icon: css`
        svg path {
          stroke: ${colors.orangeDark};
        }
      `,
    },
    teaserNavigation: {
      link: css`
        box-shadow: 0px 3px 20px 0px rgba(0, 0, 0, 0.05);
      `,
    },
    button: {
      default: css`
        background: ${colors.orangeDark};
        border: none;
        box-shadow: none;
        overflow: hidden;
        color: ${colors.white};

        &:hover,
        &:focus,
        &:active {
          background: ${colors.orangeDark};
          color: ${colors.white};
        }
      `,
      outlined: css`
        background: none;
        border-color: ${colors.orangeDark};
        color: ${colors.orangeDark};

        &:hover,
        &:focus,
        &:active {
          border-color: ${colors.orangeDark};
          background: ${colors.orangeDark};
          color: ${colors.white};
        }
      `,
    },
    intro: {
      default: css`
        color: ${colors.black};
      `,
    },
    scrollToTop: {
      icon: css`
        polygon {
          fill: ${colors.orangeDark};
          stroke: ${colors.orangeDark};
        }
      `,
    },
    slider: css`
      .swiper {
        .swiper-pagination {
          .swiper-pagination-bullet {
            border: ${rem(1)} solid ${colors.orangeDark};
            background: ${colors.white};

            &-active {
              background: ${colors.orangeDark};
            }
          }
        }
      }
    `,
    publication: {
      basket: css`
        svg path,
        svg polygon {
          fill: ${colors.orangeDark};
          stroke: ${colors.orangeDark};
        }
      `,
    },
  },
  renovabene: {
    headerLogo: css`
      ${mixins.responsiveStyles({
        width: { zero: 160, medium: 200 },
      })}
    `,
    navigation: {
      iconLink: css`
        background-color: ${colors.primarySunYellow800};
      `,
      emphasizedLink: css`
        background-color: ${colors.primarySunYellow800};
      `,
    },
    link: {
      tile: css`
        border: none;
      `,
    },
  },
  locabene: {
    headerLogo: css`
      ${mixins.responsiveStyles({
        width: { zero: 160, medium: 200 },
      })}
    `,
    link: {
      tile: css`
        border: none;
      `,
    },
  },
  'co2-rechner': {
    link: {
      tile: css`
        border: none;
      `,
    },
    navigation: {
      iconLink: css`
        background-color: ${colors.blue900};

        &,
        &:hover {
          color: ${colors.white};
        }

        svg path {
          stroke: ${colors.white};
        }
      `,
      emphasizedLink: css`
        background-color: ${colors.blue900};

        &,
        &:hover {
          color: ${colors.white};
        }

        svg path {
          stroke: ${colors.white};
        }
      `,
    },
  },
  incite: {
    link: {
      tile: css`
        border: none;
      `,
    },
  },
};

const getStylesExtension = (key?: string) => {
  const styles = stylesObj[key as keyof typeof stylesObj];

  return styles ? { stylesExtension: styles } : {};
};

export default getStylesExtension;
